import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BitrixWebhookService } from '../../../services/bitrix-webhook.service';
import { BitrixWebhookRequestDetail, BitrixRequestStatus, BitrixRequestType } from '../../../interfaces/bitrix-webhook.interfaces';
import { ToastService } from '../../../services/toast.service';

// Interface para entradas de log de procesamiento
interface ProcessingLogEntry {
  Timestamp: string;
  Level: string;
  Code?: string;
  Message: string;
}

@Component({
  selector: 'app-view-bitrix-request',
  templateUrl: './view-bitrix-request.component.html',
  styleUrls: ['./view-bitrix-request.component.scss']
})
export class ViewBitrixRequestComponent implements OnInit {
  request: BitrixWebhookRequestDetail | null = null;
  loading = false;
  rawLeadDataFormatted: string = '';
  showRawData = false;
  httpRequestInfoFormatted: string = '';
  showHttpRequestInfo = false;
  httpRequestData: any = null;
  outboundResponseFormatted: string = '';
  showOutboundResponse = false;

  // Propiedades para logs de procesamiento
  processingLogs: ProcessingLogEntry[] = [];
  showProcessingLogs = false;

  BitrixRequestStatus = BitrixRequestStatus;
  BitrixRequestType = BitrixRequestType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bitrixService: BitrixWebhookService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadRequest(parseInt(id, 10));
    }
  }

  loadRequest(id: number): void {
    this.loading = true;
    this.bitrixService.getRequestById(id).subscribe({
      next: (data) => {
        this.request = data;
        if (data.rawLeadData) {
          try {
            const parsed = JSON.parse(data.rawLeadData);
            this.rawLeadDataFormatted = JSON.stringify(parsed, null, 2);
          } catch (e) {
            this.rawLeadDataFormatted = data.rawLeadData;
          }
        }

        if (data.httpRequestInfo) {
          try {
            this.httpRequestData = JSON.parse(data.httpRequestInfo);
            this.httpRequestInfoFormatted = JSON.stringify(this.httpRequestData, null, 2);
          } catch (e) {
            this.httpRequestInfoFormatted = data.httpRequestInfo;
          }
        }

        if (data.outboundResponse) {
          try {
            const parsed = JSON.parse(data.outboundResponse);
            this.outboundResponseFormatted = JSON.stringify(parsed, null, 2);
          } catch (e) {
            this.outboundResponseFormatted = data.outboundResponse;
          }
        }

        // Parsear logs de procesamiento
        if (data.processingLogs) {
          try {
            this.processingLogs = JSON.parse(data.processingLogs);
          } catch (e) {
            this.processingLogs = [];
          }
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error cargando solicitud:', error);
        this.toastService.showError('Error cargando detalle de la solicitud');
        this.loading = false;
        this.router.navigate(['/bitrix-requests']);
      }
    });
  }

  retryRequest(): void {
    if (!this.request) return;

    if (this.request.status === BitrixRequestStatus.Success) {
      this.toastService.showWarning('Esta solicitud ya fue procesada exitosamente');
      return;
    }

    if (this.request.status === BitrixRequestStatus.Processing) {
      this.toastService.showWarning('Esta solicitud está siendo procesada actualmente');
      return;
    }

    if (confirm(`¿Está seguro de reprocesar la solicitud Lead ID: ${this.request.leadId}?\n\nAsegúrese de haber corregido el problema antes de reprocesar.`)) {
      this.bitrixService.retryProcessing(this.request.id).subscribe({
        next: (response: any) => {
          this.toastService.showSuccess(response.message);

          // Si hay una nueva solicitud creada, navegar a ella
          if (response.newRequestId) {
            this.toastService.showSuccess(`Navegando a la nueva solicitud #${response.newRequestId}...`);
            // Esperar un momento para que el procesamiento de Hangfire inicie
            setTimeout(() => {
              this.router.navigate(['/bitrix-requests', response.newRequestId]);
            }, 2000);
          } else {
            // Si no hay nueva solicitud, recargar la actual
            this.loadRequest(this.request!.id);
          }
        },
        error: (error) => {
          console.error('Error reprocesando solicitud:', error);
          this.toastService.showError(error.error || 'Error al reprocesar la solicitud');
        }
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/bitrix-requests']);
  }

  getStatusName(status: BitrixRequestStatus): string {
    return this.bitrixService.getStatusName(status);
  }

  getStatusBadgeClass(status: BitrixRequestStatus): string {
    return this.bitrixService.getStatusBadgeClass(status);
  }

  getStatusIcon(status: BitrixRequestStatus): string {
    return this.bitrixService.getStatusIcon(status);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES');
  }

  toggleRawData(): void {
    this.showRawData = !this.showRawData;
  }

  toggleHttpRequestInfo(): void {
    this.showHttpRequestInfo = !this.showHttpRequestInfo;
  }

  getHttpHeaders(): { key: string, value: string }[] {
    if (!this.httpRequestData?.headers) return [];

    return Object.entries(this.httpRequestData.headers).map(([key, value]) => ({
      key,
      value: value as string
    }));
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toastService.showSuccess('Copiado al portapapeles');
    }).catch(err => {
      console.error('Error copiando al portapapeles:', err);
      this.toastService.showError('Error al copiar');
    });
  }

  viewEnrollment(): void {
    if (this.request?.enrollmentId) {
      this.router.navigate(['/enrollments', this.request.enrollmentId]);
    }
  }

  viewParticipant(): void {
    if (this.request?.participantId) {
      this.router.navigate(['/participants', this.request.participantId]);
    }
  }

  toggleOutboundResponse(): void {
    this.showOutboundResponse = !this.showOutboundResponse;
  }

  getRequestTypeName(type: BitrixRequestType): string {
    switch (type) {
      case BitrixRequestType.Inbound: return 'Entrada';
      case BitrixRequestType.Outbound: return 'Salida';
      default: return 'Entrada';
    }
  }

  getRequestTypeBadgeClass(type: BitrixRequestType): string {
    switch (type) {
      case BitrixRequestType.Inbound: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case BitrixRequestType.Outbound: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  }

  getRequestTypeIcon(type: BitrixRequestType): string {
    switch (type) {
      case BitrixRequestType.Inbound: return 'M19 14l-7 7m0 0l-7-7m7 7V3';
      case BitrixRequestType.Outbound: return 'M5 10l7-7m0 0l7 7m-7-7v18';
      default: return 'M19 14l-7 7m0 0l-7-7m7 7V3';
    }
  }

  isOutbound(): boolean {
    return this.request?.requestType === BitrixRequestType.Outbound;
  }

  // Métodos para logs de procesamiento
  toggleProcessingLogs(): void {
    this.showProcessingLogs = !this.showProcessingLogs;
  }

  getLogLevelClass(level: string): string {
    switch (level) {
      case 'INF': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'WRN': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'ERR': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  }

  getLogLevelIcon(level: string): string {
    switch (level) {
      case 'INF': return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'WRN': return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.693-1.333-3.464 0L3.268 16c-.77 1.333.192 3 1.732 3z';
      case 'ERR': return 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z';
      default: return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    }
  }

  getLogLevelIconColor(level: string): string {
    switch (level) {
      case 'INF': return 'text-blue-500';
      case 'WRN': return 'text-yellow-500';
      case 'ERR': return 'text-red-500';
      default: return 'text-gray-500';
    }
  }

  getLogRowClass(level: string): string {
    switch (level) {
      case 'INF': return 'border-blue-200 bg-blue-50/50 dark:border-blue-800/30 dark:bg-blue-900/10';
      case 'WRN': return 'border-yellow-200 bg-yellow-50/50 dark:border-yellow-800/30 dark:bg-yellow-900/10';
      case 'ERR': return 'border-red-200 bg-red-50/50 dark:border-red-800/30 dark:bg-red-900/10';
      default: return 'border-gray-200 bg-gray-50/50 dark:border-gray-800/30 dark:bg-gray-900/10';
    }
  }

  formatLogTimestamp(timestamp: string): string {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  }

  getLogCountByLevel(level: string): number {
    return this.processingLogs.filter(l => l.Level === level).length;
  }
}

